import { Fragment } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { format } from 'date-fns';
import { useCcStore } from '../../store/ccStore';
import { geometryCentroid } from '../../lib/geo';
import { TARGET_STATUS_META, TARGET_TYPE_META, operationForTarget } from '../../types/domain';
import type { Target, TargetAssignment } from '../../types/domain';
import GeometryShape from './GeometryShape';
import {
  labelChipIcon,
  targetCrosshairIcon,
  targetStatusColor,
  targetRenderClass,
  shortCallsign,
  OPS_COLORS,
} from './opsChips';

// SCHEDULED "planned" look: red iconography kept, dimmed to ~65% + dashed ring.
const SCHEDULED_OPACITY = 0.65;

// The assignment currently in contact (execution started, not stopped).
function executingAssignment(t: Target): TargetAssignment | undefined {
  const list = t.assignments ?? [];
  return list.find((a) => a.executionStartedAt && !a.executionStoppedAt) ?? list[0];
}

// F-2 + F-5: every visible target as its geometry (status-colored) + crosshair
// marker + permanent label chip; EXECUTING targets pulse and flip the chip to a
// CONTACT banner. Fetching + unit scoping live in useOperationalMap.
export default function TargetsLayer({ targets }: { targets: Target[] }) {
  const activeExecutions = useCcStore((s) => s.activeExecutions);

  return (
    <>
      {targets.map((t) => {
        const rep =
          t.repLat != null && t.repLng != null
            ? { lat: t.repLat, lon: t.repLng }
            : geometryCentroid(t);
        if (!rep) return null;

        const statusMeta = TARGET_STATUS_META[t.status];
        const typeMeta = TARGET_TYPE_META[t.type];
        const executing = t.status === 'EXECUTING';
        const cls = targetRenderClass(t);
        // SCHEDULED (window not yet open) keeps the red target iconography,
        // rendered as a dimmed/dashed "planned" outline. Only ENDED goes gray.
        const scheduled = cls === 'scheduled';
        const dim = cls === 'ended';
        const color = scheduled ? OPS_COLORS.targetLive : targetStatusColor(t.status);
        const requiredOp = t.requiredOperation ?? operationForTarget(t.type);
        const assigned = (t.assignments ?? []).map(
          (a) => a.callsign || a.platoon?.callsign || a.platoonId?.slice(0, 6) || 'plt'
        );

        // F-5 CONTACT chip content for executing targets.
        const exec = executing ? executingAssignment(t) : undefined;
        const execLive = executing ? activeExecutions[t.id] : undefined;
        const execCallsign = shortCallsign(exec?.callsign || exec?.platoon?.callsign || execLive?.callsign);
        const execStartedAt = exec?.executionStartedAt || execLive?.startedAt || null;
        const execTime =
          execStartedAt && !Number.isNaN(new Date(execStartedAt).getTime())
            ? format(new Date(execStartedAt), 'HH:mm')
            : null;

        // Scheduled targets announce their window on the chip: "RAID TARGET · OPENS 20:00".
        const opensAt =
          scheduled && t.openingTime && !Number.isNaN(new Date(t.openingTime).getTime())
            ? format(new Date(t.openingTime), 'HH:mm')
            : null;

        const chip = executing
          ? labelChipIcon({
              title: `CONTACT — ${execCallsign} vs ${t.name.toUpperCase()}`,
              sub: `TROOPS IN CONTACT${execTime ? ` · ${execTime}` : ''}`,
              color: OPS_COLORS.targetExecuting,
            })
          : labelChipIcon({
              title: t.name.toUpperCase(),
              sub: `${requiredOp} TARGET${opensAt ? ` · OPENS ${opensAt}` : ''}`,
              color,
              dim,
            });

        // Per-object style (§17) is an override LAYERED on the status/type-derived
        // look: each present field wins; absent fields keep today's behaviour.
        // The crosshair marker + chip stay status-driven (pulse/dim untouched).
        const style = t.geometry?.style ?? t.style;

        return (
          <Fragment key={t.id}>
            <GeometryShape
              geometry={t}
              color={style?.strokeColor ?? color}
              fillColor={style?.fillColor}
              dashed={style?.dashed ?? (t.shape === 'LINE' || dim || scheduled)}
              weight={style?.weight}
              fillOpacity={style?.fillOpacity ?? (dim ? 0.05 : scheduled ? 0.06 : 0.12)}
              opacity={style?.opacity ?? (scheduled ? 0.75 : 1)}
            />
            {/* Label chip floats above the representative point. */}
            <Marker position={[rep.lat, rep.lon]} icon={chip} zIndexOffset={executing ? 1900 : 880} interactive={false} />
            <Marker
              position={[rep.lat, rep.lon]}
              icon={targetCrosshairIcon({
                color,
                dim,
                pulse: executing,
                dashed: scheduled || dim,
                opacity: scheduled ? SCHEDULED_OPACITY : undefined,
              })}
              zIndexOffset={executing ? 1800 : 900}
            >
              <Popup>
                <div className="p-1 min-w-[190px]">
                  <div className="font-bold text-base flex items-center gap-1">
                    <span>{typeMeta?.icon}</span> {t.name}
                  </div>
                  <div className="text-xs" style={{ color }}>
                    {statusMeta?.label ?? t.status}
                    {t.status === 'INACTIVE' && ' — not active yet'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {typeMeta?.label} → {requiredOp}
                  </div>
                  <div className="text-xs text-gray-600">Enemy manpower: {t.enemyManpower ?? '—'}</div>
                  {(t.openingTime || t.closingTime) && (
                    <div className="text-xs text-gray-600">
                      {t.openingTime ? format(new Date(t.openingTime), 'dd MMM HH:mm') : '—'}
                      {' → '}
                      {t.closingTime ? format(new Date(t.closingTime), 'HH:mm') : '—'}
                    </div>
                  )}
                  {assigned.length > 0 && (
                    <div className="text-xs text-gray-600 mt-1">Assigned: {assigned.join(', ')}</div>
                  )}
                </div>
              </Popup>
            </Marker>
          </Fragment>
        );
      })}
    </>
  );
}
